// Top-left mode widget (§14.3). Royale and Hot Potato: a row of 8 bean icons in identity colors;
// out beans grey out with an X, ghosts get a ghost icon, the potato carrier wears a ticking bomb
// that shakes harder as the fuse runs down (intensity only, never a number, §4.2), the crowned
// leader a paper crown, and the local bean a pointer. Teams split the row by team color. Crown
// Grab: the top-4 crown progress bars toward the goal (TUNE.crown), with your own bar always visible.
import { MAX_BEANS, NO_SLOT, TICK_HZ, TUNE } from '@shared/constants';
import { ModeId } from '@shared/ids';
import type { BeanView, FrameView } from '../../game/FrameView';
import { beanIcon, icon, identityBadge, identityHex } from '../icons';
import { el, setHidden, setStyle, setText, toggleClass } from '../ui';

const CROWN_ROWS = 4;

interface Chip { root: HTMLElement; art: HTMLElement; key: string; extra: HTMLElement }
interface CrownRow { root: HTMLElement; badge: HTMLElement; badgeKey: string; name: HTMLElement; fill: HTMLElement; secs: HTMLElement }

/** Crown Grab standings: top 4 by crown time, plus the local bean if it is not among them. */
export function crownRows(beans: readonly BeanView[], localSlot: number): BeanView[] {
  const active = beans.filter((b) => b.active).sort((a, b) => b.crownTicks - a.crownTicks || a.slot - b.slot);
  const top = active.slice(0, CROWN_ROWS);
  const me = active.find((b) => b.slot === localSlot);
  if (me && !top.includes(me)) top.push(me);
  return top;
}

export interface ModeWidget { readonly el: HTMLElement; update(view: FrameView, colorblind: boolean): void }

export function createModeWidget(): ModeWidget {
  const strip = el('div', { class: 'mw-strip' });
  const alive = el('div', { class: 'mw-alive' });
  const teamRed = el('span', { class: 'mw-team red' });
  const teamBlue = el('span', { class: 'mw-team blue' });
  const teams = el('div', { class: 'mw-teams' }, teamRed, teamBlue);
  const royale = el('div', { class: 'mw-royale' }, strip, el('div', { class: 'mw-row' }, alive, teams));
  const chips: Chip[] = Array.from({ length: MAX_BEANS }, () => {
    const art = el('span', { class: 'mw-art' });
    const extra = el('span', { class: 'mw-extra' });
    const root = el('span', { class: 'mw-bean' }, art, extra, el('span', { class: 'mw-x' }, icon('x')), el('span', { class: 'mw-you', text: 'YOU' }));
    strip.append(root);
    return { root, art, key: '', extra };
  });

  const crownHead = el('div', { class: 'mw-crown-head' }, icon('crown'), el('span', { text: `FIRST TO ${TUNE.crown.goalTicks / TICK_HZ}s` }));
  const crownList = el('div', { class: 'mw-crown-list' });
  // Teams (§4.3): team time is summed, so the race is Red vs Blue; the bean rows below show who holds.
  const teamRows = ([1, 2] as const).map((team) => {
    const fill = el('i');
    const secs = el('span', { class: 'cr-secs' });
    const root = el('div', { class: `cr-row cr-team ${team === 1 ? 'red' : 'blue'}` },
      el('span', { class: 'cr-team-name', text: team === 1 ? 'RED' : 'BLUE' }),
      el('span', { class: 'cr-bar' }, fill, el('span', { class: 'cr-crown' }, icon('crown'))), secs);
    return { team, root, fill, secs };
  });
  const crownTeams = el('div', { class: 'mw-crown-teams' }, ...teamRows.map((t) => t.root));
  crownTeams.hidden = true;
  const crown = el('div', { class: 'mw-crown' }, crownHead, crownTeams, crownList);
  const rows: CrownRow[] = Array.from({ length: CROWN_ROWS + 1 }, () => {
    const badge = el('span');
    const name = el('span', { class: 'cr-name ellipsis' });
    const fill = el('i');
    const secs = el('span', { class: 'cr-secs' });
    const root = el('div', { class: 'cr-row' }, badge, name, el('span', { class: 'cr-bar' }, fill, el('span', { class: 'cr-crown' }, icon('crown'))), secs);
    crownList.append(root);
    return { root, badge, badgeKey: '', name, fill, secs };
  });

  const root = el('div', { class: 'mode-widget' }, royale, crown);
  let lastAlive = -1;

  function chipKey(b: BeanView, colorblind: boolean): string {
    const st = b.ghost ? 'g' : b.out ? 'o' : 'a';
    return `${b.identity}:${colorblind ? 1 : 0}:${st}:${b.team}`;
  }

  function updateRoyale(view: FrameView, colorblind: boolean): void {
    let n = 0;
    for (let i = 0; i < MAX_BEANS; i++) {
      const b = view.beans[i];
      const c = chips[i];
      setHidden(c.root, !b.active);
      if (!b.active) continue;
      const key = chipKey(b, colorblind);
      if (key !== c.key) {
        const wasAlive = c.key.split(':')[2] === 'a';
        c.key = key;
        const hex = identityHex(b.identity, colorblind);
        c.art.replaceChildren(beanIcon(hex, b.ghost ? 'ghost' : b.out ? 'out' : 'happy', 'ico mw-bean-svg'), identityBadge(b.identity, colorblind, 'idbadge xs'));
        if (wasAlive && (b.out || b.ghost)) c.root.classList.add('just-out');
      }
      toggleClass(c.root, 'out', b.out && !b.ghost);
      toggleClass(c.root, 'ghost', b.ghost);
      toggleClass(c.root, 'me', b.slot === view.localSlot);
      toggleClass(c.root, 'team-red', b.team === 1);
      toggleClass(c.root, 'team-blue', b.team === 2);
      const potato = view.mode.potatoSlot === b.slot;
      const crowned = b.crowned && !potato;
      const extraKey = potato ? 'potato' : crowned ? 'crowned' : '';
      if (c.extra.dataset.k !== extraKey) {
        c.extra.dataset.k = extraKey;
        c.extra.replaceChildren(...(potato ? [icon('bomb')] : crowned ? [icon('crown')] : []));
      }
      toggleClass(c.root, 'potato', potato);
      if (potato) setStyle(c.root, '--fuse', (view.mode.potatoIntensity / TUNE.potato.intensityMax).toFixed(2));
      if (!b.out && !b.ghost) n++;
    }
    // Teams (§4.5): beans left per side.
    let red = 0;
    let blue = 0;
    let anyTeam = false;
    for (const b of view.beans) {
      if (!b.active || b.team === 0) continue;
      anyTeam = true;
      if (b.out || b.ghost) continue;
      if (b.team === 1) red++;
      else blue++;
    }
    setHidden(teams, !anyTeam);
    if (anyTeam) {
      setText(teamRed, `RED ${red}`);
      setText(teamBlue, `BLUE ${blue}`);
    }
    const left = view.mode.alive > 0 ? view.mode.alive : n;
    if (left !== lastAlive) {
      lastAlive = left;
      setText(alive, `${left} LEFT`);
      alive.classList.remove('tick');
      void alive.offsetWidth;
      alive.classList.add('tick');
    }
  }

  function updateCrown(view: FrameView, colorblind: boolean): void {
    const list = crownRows(view.beans, view.localSlot);
    const goal = view.mode.crownGoalTicks > 0 ? view.mode.crownGoalTicks : TUNE.crown.goalTicks;
    const teamTicks = [0, 0, 0];
    let anyTeam = false;
    for (const b of view.beans) {
      if (!b.active || (b.team !== 1 && b.team !== 2)) continue;
      anyTeam = true;
      teamTicks[b.team] += b.crownTicks;
    }
    setHidden(crownTeams, !anyTeam);
    if (anyTeam) {
      const holderTeam = view.mode.crownHolder !== NO_SLOT ? view.beans[view.mode.crownHolder]?.team ?? 0 : 0;
      for (const t of teamRows) {
        setStyle(t.fill, 'width', `${(Math.min(1, teamTicks[t.team] / goal) * 100).toFixed(1)}%`);
        setText(t.secs, `${(teamTicks[t.team] / TICK_HZ).toFixed(1)}s`);
        toggleClass(t.root, 'holder', holderTeam === t.team);
        toggleClass(t.root, 'me', view.localSlot !== NO_SLOT && view.beans[view.localSlot]?.team === t.team);
      }
    }
    rows.forEach((r, i) => {
      const b = list[i];
      setHidden(r.root, !b);
      if (!b) return;
      const bk = `${b.identity}:${colorblind}`;
      if (bk !== r.badgeKey) {
        r.badgeKey = bk;
        const nb = identityBadge(b.identity, colorblind, 'idbadge sm');
        r.badge.replaceWith(nb);
        r.badge = nb;
      }
      setText(r.name, b.name || `Bean ${b.slot + 1}`);
      const k = Math.min(1, b.crownTicks / goal);
      setStyle(r.fill, 'width', `${(k * 100).toFixed(1)}%`);
      setStyle(r.root, '--id', identityHex(b.identity, colorblind));
      setText(r.secs, `${(b.crownTicks / TICK_HZ).toFixed(1)}s`);
      toggleClass(r.root, 'holder', view.mode.crownHolder === b.slot && b.slot !== NO_SLOT);
      toggleClass(r.root, 'me', b.slot === view.localSlot);
      toggleClass(r.root, 'extra', i === CROWN_ROWS);
      toggleClass(r.root, 'team-red', b.team === 1);
      toggleClass(r.root, 'team-blue', b.team === 2);
    });
  }

  return {
    el: root,
    update(view, colorblind) {
      const mode = view.modeId;
      const isCrown = mode === ModeId.CROWN_GRAB;
      const isLastStanding = mode === ModeId.ROYALE || mode === ModeId.HOT_POTATO;
      setHidden(root, !isCrown && !isLastStanding);
      setHidden(royale, !isLastStanding);
      setHidden(crown, !isCrown);
      if (isLastStanding) updateRoyale(view, colorblind);
      else if (isCrown) updateCrown(view, colorblind);
    },
  };
}
